import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { AlertSummaryCounts } from '../lib/types';

export interface AlertSummary {
  /** Exact total of unacknowledged alerts (from the API, not page-local). */
  count: number;
  /** Highest severity present among unacknowledged alerts; drives the badge tint. */
  worst: 'CRITICAL' | 'WARNING' | 'INFO';
}

// Shared summary of the unacknowledged-alert backlog for the sidebar indicator.
// Keyed under ['alerts', …] so the Alerts page's invalidateQueries({ queryKey: ['alerts'] })
// after an acknowledge refetches this too — the sidebar stays in sync for free.
//
// COUNTS, NOT A PAGE OF ROWS. This used to fetch
// `/alerts?acknowledged=false&limit=100` and scan those rows for the worst
// severity, which made the tint page-local: a CRITICAL outside the hundred most
// recently updated unacknowledged rows left the badge showing WARNING. The
// count was never wrong — it came from the API's `total` — so the badge said
// the right NUMBER in the wrong COLOUR, which is the half a reader acts on.
// `/alerts/summary` answers from a `GROUP BY severity`, so it cannot miss a row
// at any volume, and the payload is three integers rather than a hundred alerts
// every sixty seconds.
export function useAlertSummary(): AlertSummary {
  const q = useQuery({
    queryKey: ['alerts', 'summary'],
    queryFn: () => api.get<AlertSummaryCounts>('/alerts/summary?acknowledged=false'),
    refetchInterval: 60_000,
  });

  const counts = q.data?.bySeverity;
  const worst: AlertSummary['worst'] =
    (counts?.CRITICAL ?? 0) > 0 ? 'CRITICAL' : (counts?.WARNING ?? 0) > 0 ? 'WARNING' : 'INFO';

  return { count: q.data?.total ?? 0, worst };
}
